import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { trackEvent } from "@/utils/analytics";
import { useContact } from "@/context/ContactContext";
import { useBooking } from "@/context/BookingContext";
import "./ContactSection.scss";

const MailIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" />
  </svg>
);
const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
  </svg>
);
const ChatIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

const ContactSection: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const { openBooking } = useBooking();
  const [formData, setFormData] = useState({ name: "", email: "", company: "", message: "", company_url: "" });
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const { prefillMessage, consumePrefill } = useContact();
  const messageRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!prefillMessage) return;
    setFormData(prev => {
      if (prev.message) return prev;
      return { ...prev, message: prefillMessage };
    });
    messageRef.current?.focus();
    consumePrefill();
  }, [prefillMessage, consumePrefill]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackEvent("contact_submit");
    setStatus("sending");
    setErrorMessage("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        let msg = t("contact.form.errorFallback");
        try { const d = await res.json(); msg = d.error || msg; } catch {}
        throw new Error(msg);
      }
      trackEvent("contact_result", { status: "success" });
      setStatus("success");
      setFormData({ name: "", email: "", company: "", message: "", company_url: "" });
    } catch (err: unknown) {
      trackEvent("contact_result", { status: "error" });
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : t("contact.form.errorFallback"));
    }
  };

  return (
    <section className="contact-section" ref={ref}>
      <div id="contact-section" className="container">
        <div className="contact-grid">
          <div className={`reveal${isVisible ? " in" : ""}`}>
            <span className="sec-label">{t("contact.label")}</span>
            <h2 className="sec-title">
              {(() => {
                const title = t("contact.title");
                const hl = t("contact.titleHighlight");
                const [before, after = ""] = title.split(hl);
                return <>{before}<em>{hl}</em>{after}</>;
              })()}
            </h2>
            <p className="contact-desc">{t("contact.desc")}</p>
            <div className="contact-points">
              <div className="contact-point">
                <span className="contact-point-icon"><MailIcon /></span>
                hello@devbloom.dev
              </div>
              <div className="contact-point">
                <span className="contact-point-icon"><ClockIcon /></span>
                {t("contact.responseTime")}
              </div>
              <div className="contact-point">
                <span className="contact-point-icon"><ChatIcon /></span>
                {t("contact.freeCall")}
              </div>
            </div>
          </div>

          <div className={`contact-book-cta reveal${isVisible ? " in" : ""}`} style={{ transitionDelay: "0.06s" }}>
            <button
              className="btn btn-accent"
              onClick={() => { trackEvent("cta_click", { location: "contact" }); openBooking("contact"); }}
              type="button"
            >
              {t("booking.cta")} <ArrowIcon />
            </button>
            <p className="contact-book-or">{t("contact.responseTime")}</p>
          </div>

          <form className={`contact-form reveal${isVisible ? " in" : ""}`} onSubmit={handleSubmit} style={{ transitionDelay: "0.12s" }}>
            <div className="form-row">
              <div className="field">
                <label htmlFor="name">{t("contact.form.name")}</label>
                <input id="name" name="name" value={formData.name} onChange={handleChange} placeholder={t("contact.form.namePlaceholder")} required />
              </div>
              <div className="field">
                <label htmlFor="email">{t("contact.form.email")}</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder={t("contact.form.emailPlaceholder")} required />
              </div>
            </div>
            <div className="field">
              <label htmlFor="company">{t("contact.form.company")}</label>
              <input id="company" name="company" value={formData.company} onChange={handleChange} placeholder={t("contact.form.companyPlaceholder")} />
            </div>
            <div className="field">
              <label htmlFor="message">{t("contact.form.message")}</label>
              <textarea id="message" name="message" ref={messageRef} value={formData.message} onChange={handleChange} placeholder={t("contact.form.messagePlaceholder")} required />
            </div>
            {/* honeypot — bots fill this; humans never see it */}
            <div className="visually-hidden" aria-hidden="true">
              <input
                type="text"
                name="company_url"
                value={formData.company_url}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <button className="form-submit" type="submit" disabled={status === "sending"}>
              {status === "sending" ? t("contact.form.sending") : t("contact.form.submit")}
              <ArrowIcon />
            </button>
            {status === "success" && <div className="form-success">{t("contact.form.success")}</div>}
            {status === "error" && <div className="form-error">{errorMessage}</div>}
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
